import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ExternalLink, Download, Image as ImageIcon, FileText, Loader2 } from "lucide-react";

export default function StorePublicUrl() {
  const [store, setStore] = useState(null);
  const [busy, setBusy] = useState(null); // 'png' | 'pdf' | null
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => { api.get("/store/me").then((r) => setStore(r.data)); }, []);
  useEffect(() => {
    if (!store) return;
    let cancelled = false;
    let objUrl = "";
    (async () => {
      try {
        const res = await api.get(`/store/qr-poster/preview`, {
          params: { origin: window.location.origin, format: "png" },
          responseType: "blob",
        });
        if (cancelled) return;
        objUrl = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
        setPreviewUrl(objUrl);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [store]);
  if (!store) return null;
  const url = `${window.location.origin}/s/${store.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(url)}&margin=10&color=0F172A&bgcolor=FFFFFF`;

  const downloadPoster = async (format) => {
    setBusy(format);
    try {
      const origin = window.location.origin;
      const res = await api.get(`/store/qr-poster/preview`, {
        params: { origin, format },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "image/png" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${store.slug}-qr-poster.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Poster downloaded (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Failed to download poster");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-public-url-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Public URL & QR</h1>
        <p className="text-slate-500">Print your branded poster and place it at your counter — customers scan and start printing in one tap.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display">Your URL</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-lg font-mono text-sm break-all border border-slate-200" data-testid="public-url">{url}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }} data-testid="copy-url"><Copy className="w-4 h-4 mr-2" />Copy</Button>
              <Button variant="outline" onClick={() => window.open(url, "_blank")} data-testid="open-url"><ExternalLink className="w-4 h-4 mr-2" />Open</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display">Plain QR Code</CardTitle></CardHeader>
          <CardContent className="text-center space-y-3">
            <img src={qrUrl} alt="QR" className="mx-auto rounded-lg border border-slate-200" data-testid="qr-image" />
            <a href={qrUrl} download={`${store.slug}-qr.png`}><Button variant="outline" data-testid="download-qr"><Download className="w-4 h-4 mr-2" />Download PNG</Button></a>
          </CardContent>
        </Card>
      </div>

      {/* Branded Poster Card */}
      <Card className="border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50/50 to-white overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                Print-Ready Branded Poster
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">Full-page A4 poster with your store's QR embedded. Print & paste at the counter.</p>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-orange-600 bg-orange-100 border border-orange-200 rounded-full px-2 py-0.5 self-start sm:self-auto">
              1024 × 1536 · Print quality
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm min-h-[240px] grid place-items-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Branded QR poster preview"
                  className="w-full object-contain max-h-[520px]"
                  data-testid="poster-preview"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-xs">Generating preview…</span>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Includes</div>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  <li className="flex gap-2"><span className="text-orange-500">•</span>Your store URL embedded as a scannable QR</li>
                  <li className="flex gap-2"><span className="text-orange-500">•</span>Print Plus branding · "Scan · Upload · Print"</li>
                  <li className="flex gap-2"><span className="text-orange-500">•</span>3-step visual guide for first-time customers</li>
                  <li className="flex gap-2"><span className="text-orange-500">•</span>Trust badges: Fast, Secure, Any Device</li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => downloadPoster("png")}
                  disabled={busy !== null}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 h-11 font-semibold"
                  data-testid="download-poster-png"
                >
                  {busy === "png" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                  Download Poster · PNG
                </Button>
                <Button
                  onClick={() => downloadPoster("pdf")}
                  disabled={busy !== null}
                  variant="outline"
                  className="flex-1 h-11 font-semibold border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="download-poster-pdf"
                >
                  {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Download Poster · PDF
                </Button>
              </div>
              <div className="text-[11px] text-slate-400 leading-relaxed">
                Tip: print on A4 or A3 glossy paper for the crispest QR. The QR is generated with high error-correction, so it stays scannable even if a corner tears.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
